/**
 * HTML to AST Parser
 * Converts clean, well-formed HTML into AST structure
 */

import {
  Block,
  ContentAST,
  EncryptedNode,
  InlineContent,
  LinkNode,
  ListItem,
  Mark,
  TextDirection,
  TextNode,
  createDivider,
  createEmptyAST,
  createEncryptedNode,
  createHeading,
  createImage,
  createLinkNode,
  createList,
  createListItem,
  createParagraph,
  createQuote,
  createTextNode,
  generateEncryptionId,
} from '../index'

export interface HtmlToAstOptions {
  defaultLang?: 'he' | 'en' | 'mixed'
  defaultDir?: TextDirection
  preserveClasses?: boolean // Whether to preserve encryption CSS classes
  cleanupWhitespace?: boolean
}

export interface HtmlToAstResult {
  success: boolean
  ast?: ContentAST
  errors: HtmlToAstError[]
}

export interface HtmlToAstError {
  message: string
  code: string
  element?: string
}

/**
 * HTML to AST Parser Class
 * Internal implementation for parsing HTML into AST structure
 */
export class HtmlToAstParser {
  private options: Required<HtmlToAstOptions>
  private errors: HtmlToAstError[] = []
  private doc: Document | null = null

  constructor(options: HtmlToAstOptions = {}) {
    this.options = {
      defaultLang: 'mixed',
      defaultDir: 'ltr',
      preserveClasses: true,
      cleanupWhitespace: true,
      ...options,
    }
  }

  /**
   * Parses external HTML content to AST
   */
  public parse(externalHtml: string): HtmlToAstResult {
    this.errors = []

    try {
      // Create a temporary DOM for parsing
      this.doc = this.createTemporaryDOM(externalHtml)

      // Extract metadata from root element if present
      const rootElement = this.doc.body.firstElementChild
      const metadata = this.extractMetadata(rootElement)

      // Create base AST
      const ast = createEmptyAST(metadata.lang, metadata.dir)
      if (metadata.title) {
        ast.meta.title = metadata.title
      }

      // Parse blocks from body content
      ast.blocks = this.parseElements(this.doc.body.children)

      return {
        success: this.errors.length === 0,
        ast,
        errors: this.errors,
      }
    } catch (error) {
      this.addError(`Failed to parse HTML: ${error}`, 'parse_error')
      return {
        success: false,
        errors: this.errors,
      }
    }
  }

  /**
   * Creates a temporary DOM for parsing external HTML
   */
  private createTemporaryDOM(externalHtml: string): Document {
    const parser = new DOMParser()
    let wrappedContent = externalHtml

    // If not a complete document, wrap in body
    if (!externalHtml.includes('<html') && !externalHtml.includes('<body')) {
      wrappedContent = `<html><body>${externalHtml}</body></html>`
    }

    const doc = parser.parseFromString(wrappedContent, 'text/html')

    if (!doc.body) {
      throw new Error('Invalid external HTML: no body element found')
    }

    return doc
  }

  /**
   * Extracts metadata from root element
   */
  private extractMetadata(rootElement: Element | null): {
    lang: 'he' | 'en' | 'mixed'
    dir: TextDirection
    title?: string
  } {
    const metadata = {
      lang: this.options.defaultLang,
      dir: this.options.defaultDir,
    }

    if (rootElement) {
      const lang = rootElement.getAttribute('lang')
      if (lang === 'he' || lang === 'en' || lang === 'mixed') {
        metadata.lang = lang
      }

      const dir = rootElement.getAttribute('dir')
      if (dir === 'rtl' || dir === 'ltr') {
        metadata.dir = dir
      }

      const title = rootElement.getAttribute('data-title')
      if (title) {
        return { ...metadata, title }
      }
    }

    return metadata
  }

  /**
   * Parses HTML elements to blocks
   */
  private parseElements(elements: HTMLCollection): Block[] {
    const blocks: Block[] = []

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i] as Element
      const block = this.parseElement(element)

      if (block) {
        blocks.push(block)
      }
    }

    return blocks
  }

  /**
   * Parses a single HTML element to a block
   */
  private parseElement(element: Element): Block | null {
    const tagName = element.tagName.toLowerCase()

    switch (tagName) {
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        return this.parseHeading(element)

      case 'p':
        return this.parseParagraph(element)

      case 'ul':
      case 'ol':
        return this.parseList(element)

      case 'blockquote':
        return this.parseQuote(element)

      case 'hr':
        return this.parseDivider(element)

      case 'img':
        return this.parseImage(element)

      case 'div':
        // Handle div containers
        if (element.children.length === 0) {
          // Empty div, treat as paragraph
          return this.parseParagraph(element)
        } else {
          // Div with children, this is tricky - for now treat as paragraph
          // In a real implementation you might want to unwrap divs
          return this.parseParagraph(element)
        }

      default:
        this.addError(`Unsupported element type: ${tagName}`, 'unsupported_element', tagName)
        return null
    }
  }

  /**
   * Parses a heading element
   */
  private parseHeading(element: Element): Block {
    const level = parseInt(element.tagName.slice(1)) as 1 | 2 | 3 | 4 | 5 | 6
    const content = this.parseInlineElements(element)

    const heading = createHeading(level)
    heading.content = content
    heading.attrs = this.parseBlockAttributes(element)

    return heading
  }

  /**
   * Parses a paragraph element
   */
  private parseParagraph(element: Element): Block {
    const content = this.parseInlineElements(element)

    const paragraph = createParagraph()
    paragraph.content = content
    paragraph.attrs = this.parseBlockAttributes(element)

    return paragraph
  }

  /**
   * Parses a list element
   */
  private parseList(element: Element): Block {
    const ordered = element.tagName.toLowerCase() === 'ol'
    const items = this.parseListItems(element.children)

    const list = createList(ordered)
    list.items = items
    list.attrs = this.parseListAttributes(element)

    return list
  }

  /**
   * Parses list items
   */
  private parseListItems(elements: HTMLCollection): ListItem[] {
    const items: ListItem[] = []

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i] as Element

      if (element.tagName.toLowerCase() !== 'li') {
        continue
      }

      const item: ListItem = {
        ...createListItem(),
        content: this.parseInlineElements(element, ['ul', 'ol']), // Exclude nested lists
        attrs: this.parseBlockAttributes(element),
      }

      // Look for nested lists
      const nestedLists = Array.from(element.children).filter(
        (child) => child.tagName && ['UL', 'OL'].includes(child.tagName.toUpperCase())
      )
      
      if (nestedLists.length > 0) {
        const nestedList = nestedLists[0] as Element
        const nestedItems = this.parseListItems(nestedList.children)
        item.children = nestedItems
      }

      items.push(item)
    }

    return items
  }

  /**
   * Parses a quote element
   */
  private parseQuote(element: Element): Block {
    const content = this.parseInlineElements(element)

    const quote = createQuote()
    quote.content = content
    quote.attrs = this.parseBlockAttributes(element)

    return quote
  }

  /**
   * Parses a divider element
   */
  private parseDivider(element: Element): Block {
    const divider = createDivider()
    divider.attrs = this.parseDividerAttributes(element)

    return divider
  }

  /**
   * Parses an image element
   */
  private parseImage(element: Element): Block {
    const src = element.getAttribute('src') || ''
    const alt = element.getAttribute('alt') || undefined

    // Extract width and height from attributes or style
    let width: number | undefined
    let height: number | undefined

    const widthAttr = element.getAttribute('width')
    if (widthAttr) {
      const parsed = parseInt(widthAttr, 10)
      if (!isNaN(parsed)) {
        width = parsed
      }
    }

    const heightAttr = element.getAttribute('height')
    if (heightAttr) {
      const parsed = parseInt(heightAttr, 10)
      if (!isNaN(parsed)) {
        height = parsed
      }
    }

    // Parse style attribute for dimensions if not set
    const style = element.getAttribute('style')
    if (style) {
      if (!width) {
        const widthMatch = style.match(/width:\s*(\d+)px/)
        if (widthMatch) {
          width = parseInt(widthMatch[1], 10)
        }
      }
      if (!height) {
        const heightMatch = style.match(/height:\s*(\d+)px/)
        if (heightMatch) {
          height = parseInt(heightMatch[1], 10)
        }
      }
    }

    // Parse alignment from style or parent
    const attrs = this.parseBlockAttributes(element)

    const image = createImage(src, {
      alt,
      width,
      height,
      attrs,
    })

    return image
  }

  /**
   * Parses inline elements to inline content
   */
  private parseInlineElements(element: Element, excludeTags: string[] = []): InlineContent[] {
    const content: InlineContent[] = []
    const childNodes = Array.from(element.childNodes)

    for (const node of childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || ''
        if (text.trim() || !this.options.cleanupWhitespace) {
          content.push(createTextNode(text))
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const elem = node as Element
        const tagName = elem.tagName.toLowerCase()

        if (excludeTags.includes(tagName)) {
          continue
        }

        const inlineNode = this.parseInlineElement(elem)
        if (inlineNode) {
          content.push(...inlineNode)
        }
      }
    }

    return content
  }

  /**
   * Parses a single inline element
   */
  private parseInlineElement(element: Element): InlineContent[] {
    const tagName = element.tagName.toLowerCase()

    // Check for encryption markers
    if (this.options.preserveClasses) {
      if (element.classList.contains('marked-for-encryption')) {
        return [this.parseMarkedEncryption(element)]
      }

      if (element.classList.contains('encrypted-text')) {
        return [this.parseEncryptedText(element)]
      }
    }

    // Handle link elements
    if (tagName === 'a') {
      return [this.parseLinkElement(element)]
    }

    // Handle formatting elements
    const marks = this.getMarksFromElement(element)
    const childContent = this.parseInlineElements(element)

    // Apply marks to all text nodes in children
    return this.applyMarksToContent(childContent, marks)
  }

  /**
   * Parses marked-for-encryption element
   */
  private parseMarkedEncryption(element: Element): EncryptedNode {
    const sectionId = element.getAttribute('data-section-id') || generateEncryptionId()
    const content = this.parseInlineElements(element)

    return createEncryptedNode(content as TextNode[], sectionId)
  }

  /**
   * Parses encrypted-text element
   */
  private parseEncryptedText(element: Element): EncryptedNode {
    const vaultId = element.getAttribute('data-vault-id') || generateEncryptionId()
    const content = [createTextNode(element.textContent || '[ENCRYPTED]')]

    return createEncryptedNode(content, vaultId)
  }

  /**
   * Parses link element
   */
  private parseLinkElement(element: Element): LinkNode {
    const href = element.getAttribute('href') || ''
    const text = element.textContent || ''
    
    // Parse any formatting within the link
    const childContent = this.parseInlineElements(element)
    
    // Extract marks from the first text node if available
    const firstTextNode = childContent.find(node => node.type === 'text') as TextNode
    const marks = firstTextNode?.marks
    
    // Create link node with the text content and any formatting
    return createLinkNode(href, text, marks)
  }

  /**
   * Gets formatting marks from HTML element
   */
  private getMarksFromElement(element: Element): Mark[] {
    const marks: Mark[] = []
    const tagName = element.tagName.toLowerCase()

    switch (tagName) {
      case 'strong':
      case 'b':
        marks.push('bold')
        break
      case 'em':
      case 'i':
        marks.push('italic')
        break
      case 'u':
        marks.push('underline')
        break
    }

    // Check for style attributes
    const style = element.getAttribute('style')
    if (style) {
      const colorMatch = style.match(/color:\s*([^;]+)/)
      if (colorMatch) {
        marks.push({ type: 'color', value: colorMatch[1].trim() })
      }

      const fontMatch = style.match(/font-family:\s*([^;]+)/)
      if (fontMatch) {
        const family = fontMatch[1].trim().replace(/['"]/g, '')
        if (['Arial', 'Times', 'Helvetica', 'Georgia', 'Verdana'].includes(family)) {
          marks.push({ type: 'font', family: family as any })
        }
      }
    }

    return marks
  }

  /**
   * Applies marks to inline content
   */
  private applyMarksToContent(content: InlineContent[], marks: Mark[]): InlineContent[] {
    if (marks.length === 0) {
      return content
    }

    return content.map((node) => {
      if (node.type === 'text') {
        const existingMarks = node.marks || []
        return {
          ...node,
          marks: [...existingMarks, ...marks],
        }
      }
      return node
    })
  }

  /**
   * Parses block-level attributes
   */
  private parseBlockAttributes(element: Element): any {
    const attrs: any = {}

    const dir = element.getAttribute('dir')
    if (dir === 'rtl' || dir === 'ltr') {
      attrs.dir = dir
    }

    const style = element.getAttribute('style')
    if (style) {
      const alignMatch = style.match(/text-align:\s*([^;]+)/)
      if (alignMatch) {
        const align = alignMatch[1].trim()
        if (['left', 'right', 'center'].includes(align)) {
          attrs.align = align
        }
      }
    }

    return Object.keys(attrs).length > 0 ? attrs : undefined
  }

  /**
   * Parses list-specific attributes
   */
  private parseListAttributes(element: Element): any {
    const attrs: any = {}

    const dir = element.getAttribute('dir')
    if (dir === 'rtl' || dir === 'ltr') {
      attrs.dir = dir
    }

    return Object.keys(attrs).length > 0 ? attrs : undefined
  }

  /**
   * Parses divider-specific attributes
   */
  private parseDividerAttributes(element: Element): any {
    const attrs: any = {}

    const style = element.getAttribute('style')
    if (style) {
      const alignMatch = style.match(/text-align:\s*([^;]+)/)
      if (alignMatch) {
        const align = alignMatch[1].trim()
        if (['left', 'right', 'center'].includes(align)) {
          attrs.align = align
        }
      }
    }

    return Object.keys(attrs).length > 0 ? attrs : undefined
  }

  /**
   * Adds a parse error
   */
  private addError(message: string, code: string, element?: string): void {
    this.errors.push({ message, code, element })
  }
}

/**
 * Main entry point: Parse HTML to AST
 */
export const parseHtmlToAst = (html: string, options?: HtmlToAstOptions): HtmlToAstResult => {
  const parser = new HtmlToAstParser(options)
  return parser.parse(html)
}

/**
 * Strict mode: Parse HTML to AST or throw error
 */
export const parseHtmlToAstStrict = (html: string, options?: HtmlToAstOptions): ContentAST => {
  const result = parseHtmlToAst(html, options)
  if (!result.success || !result.ast) {
    const errorMessages = result.errors.map((e) => e.message).join('\n')
    throw new Error(`Failed to parse HTML to AST:\n${errorMessages}`)
  }
  return result.ast
}
